import React from 'react';
import { EVENTS } from '../../analytics/trackEvent';
import { formatInventoryKey } from '../../i18n/formatInventoryKey';
import { useI18n } from '../../i18n/I18nContext';
import { LegoBaseplate } from './LegoBaseplate';
import { BaseplateSize, LegoSize } from '../../types/LegoTypes';
import { calculateBrickInventory } from '../../utils/legoPatterns';
import { splitNameIntoBaseplates } from '../../utils/legoLayout';
import {
  AmazonRegion,
  getAmazonRegion,
  getCountryCodeFromLocale,
  normalizeCountryCode,
} from '../../utils/rakutenRegion';
import './LegoBuilder.css';

interface LegoPreviewProps {
  name: string;
  size: LegoSize;
  baseplate: BaseplateSize;
}

const AMAZON_US_AFFILIATE_ID = 'omniasolution-20';
const AMAZON_ES_AFFILIATE_ID = 'omniasolution-21';
const AMAZON_SMALL_SET_QUERY = 'LEGO Classic 11013';
const AMAZON_LARGE_SET_QUERY = 'LEGO Classic 10698';
const GEOLOCATION_CACHE_KEY = 'affiliate-country-code';
const GEOLOCATION_ENDPOINT = 'https://ipapi.co/json/';

function buildAmazonAffiliateUrl(region: AmazonRegion, query: string): string {
  const marketplaceConfig: Record<AmazonRegion, { baseUrl: string; affiliateId?: string }> = {
    us: {
      baseUrl: 'https://www.amazon.com/s',
      affiliateId: AMAZON_US_AFFILIATE_ID,
    },
    es: {
      baseUrl: 'https://www.amazon.es/s',
      affiliateId: AMAZON_ES_AFFILIATE_ID,
    },
    mx: {
      baseUrl: 'https://www.amazon.com/s',
      affiliateId: AMAZON_US_AFFILIATE_ID,
    },
    intl: {
      baseUrl: 'https://www.amazon.com/s',
      affiliateId: AMAZON_US_AFFILIATE_ID,
    },
  };

  const { baseUrl, affiliateId } = marketplaceConfig[region];
  const url = new URL(baseUrl);
  url.searchParams.set('k', query);
  if (affiliateId) {
    url.searchParams.set('tag', affiliateId);
  }
  return url.toString();
}

async function detectCountryCode(): Promise<string | null> {
  if (typeof window === 'undefined') {
    return null;
  }

  const cachedCountry = window.sessionStorage.getItem(GEOLOCATION_CACHE_KEY);
  if (cachedCountry) {
    return cachedCountry;
  }

  try {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 2500);
    const response = await window.fetch(GEOLOCATION_ENDPOINT, {
      signal: controller.signal,
    });
    window.clearTimeout(timeoutId);

    if (!response.ok) {
      return getCountryCodeFromLocale(window.navigator.language);
    }

    const data = await response.json();
    const countryCode =
      typeof data?.country_code === 'string'
        ? data.country_code
        : typeof data?.countryCode === 'string'
          ? data.countryCode
          : typeof data?.country === 'string'
            ? data.country
            : null;

    const normalizedCountry =
      normalizeCountryCode(countryCode) ?? getCountryCodeFromLocale(window.navigator.language);

    if (normalizedCountry) {
      window.sessionStorage.setItem(GEOLOCATION_CACHE_KEY, normalizedCountry);
      return normalizedCountry;
    }
  } catch (_error) {
    return getCountryCodeFromLocale(window.navigator.language);
  }

  return getCountryCodeFromLocale(window.navigator.language);
}

export const LegoPreview: React.FC<LegoPreviewProps> = ({ name, size, baseplate }) => {
  const { t } = useI18n();
  const inventory = calculateBrickInventory(name, size, baseplate);
  const baseplates = splitNameIntoBaseplates(name, size, baseplate);
  const affiliateRef = React.useRef<HTMLDivElement>(null);
  const [countryCode, setCountryCode] = React.useState<string | null>(() =>
    typeof window !== 'undefined' ? getCountryCodeFromLocale(window.navigator.language) : null,
  );
  const totalPieces = Object.values(inventory).reduce((sum, count) => sum + count, 0);
  const amazonQuery = totalPieces < 60 ? AMAZON_SMALL_SET_QUERY : AMAZON_LARGE_SET_QUERY;
  const amazonRegion = getAmazonRegion(countryCode);
  const amazonUrl = buildAmazonAffiliateUrl(amazonRegion, amazonQuery);
  const amazonSet = totalPieces < 60 ? 'small' : 'large';

  React.useEffect(() => {
    const affiliateSection = affiliateRef.current;
    if (!affiliateSection || typeof window === 'undefined') {
      return undefined;
    }

    let isMounted = true;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        observer.disconnect();

        void detectCountryCode().then((detectedCountry) => {
          if (isMounted && detectedCountry) {
            setCountryCode(detectedCountry);
          }
        });
      },
      { rootMargin: '120px' },
    );

    observer.observe(affiliateSection);

    return () => {
      isMounted = false;
      observer.disconnect();
    };
  }, []);

  return (
    <div className='lego-preview' data-size={size.toLowerCase()}>
      {baseplates.map((plateRows, baseIndex) => (
        <div key={`baseplate-${baseIndex}`} className='lego-baseplate-container'>
          <LegoBaseplate
            key={`baseplate-${baseIndex}`}
            rows={plateRows}
            size={size}
            baseplate={baseplate}
          />
        </div>
      ))}
      <div className='brick-inventory'>
        <h3>{t.piecesNeeded}</h3>
        <ul>
          {Object.entries(inventory).map(([key, count]) => (
            <li key={key} className='inventory-item'>
              <span className='piece-count'>{count}x</span>
              <span className='piece-description'>{formatInventoryKey(key, t)}</span>
            </li>
          ))}
        </ul>
        <div className='affiliate-build-box' ref={affiliateRef}>
          <div className='affiliate-build-divider' aria-hidden='true' />
          <div className='affiliate-build-content'>
            <h4>{t.affiliateBuildTitle}</h4>
            <div className='affiliate-build-actions'>
              <a
                className='affiliate-button affiliate-button--primary'
                href={amazonUrl}
                target='_blank'
                rel='noopener noreferrer sponsored'
                data-umami-event={EVENTS.AFFILIATE_AMAZON}
                data-umami-event-source='pieces-needed-card'
                data-umami-event-target='amazon'
                data-umami-event-region={amazonRegion}
                data-umami-event-set={amazonSet}
                data-umami-event-piece-total={totalPieces}
              >
                {t.affiliateAmazonCta}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
